/**
 * Step 3 In funnel
 * View and select offset wells
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import {
  AppBar, Toolbar, Typography, Drawer, Box, Select, MenuItem, Stack,
  FormControl, TextField, InputLabel, Button, IconButton,
  Grid, Card, CssBaseline, Container, Checkbox,
  Tabs, Tab, FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import html2canvas from 'html2canvas';

import Header from '../../components/Header';
import Map from '../../components/Map';
import Search from '../../components/Search';
import Cube from '../../components/Cube';
import FpzTable from '../../components/FpzTable';

import useCartofactWells from '../../hooks/useCartofactWells';
import {
  getSurveysIntersectingCylinder,
  metersToDegrees,
  generateSimpleDirectionalSurvey,
  generateFpzPolygon,
  padBboxDegrees
} from '../../common/functions';
import { gisActions, fpzActions } from '../../actions';
import { SET_DIRECTIONAL_SURVEYS } from '../../actions/types';

const drawerWidth = 240;

const containerStyle = {
  width: '100vw',
  height: '100vh',
  position: 'relative'
};

const mapContainerStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 1
};

const OffsetPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cubeRef = useRef();
  const mapCardRef = useRef();


  const {
    filter,
    cartofactWells,
    selectedTreatmentWells,
    ...fpzState
  } = useSelector(state => state.fpzReducer);

  const workingWellsRef = useRef([]);

  const [toggleCartofactWells, setToggleCartofactWells] = useState(true);

  useCartofactWells({
    toggleCartofactWells: toggleCartofactWells,
    boundingBox: fpzState?.map?.boundingBox,
    filter,
  });

  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);
  const [cubeRender, setCubeRender] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  const [selected, setSelected] = useState(0);


  // Cube
  const [toggleFpzRadius, setToggleFpzRadius] = useState(true);
  const [toggleOffsetWells, setToggleOffsetWells] = useState(true);
  const [toggleRotate, setToggleRotate] = useState(false);
  const [mapImageDataUrl, setMapImageDataUrl] = useState("");
  const [cubeImageDataUrl, setCubeImageDataUrl] = useState("");

  const defaultFpzRadius = 500;
  const defaultFpzRadiusColor = '#66FFFF';

  const fetchOffsetWells = async () => {
    const wells = workingWellsRef.current;

    for (const [index, well] of wells.entries()) {
      console.log('selectedTreatmentWells.well', well);
      const fpzRadius = well?.fpzRadiuses?.length > 0 ? well.fpzRadiuses[well.fpzRadiuses.length - 1]?.radius : defaultFpzRadius;
      console.log('fpzRadius', fpzRadius);

      // Fetch offset wells for each treatment well based off intersection with FPZ Radius
      // If offsetwell page has been accessed and offset wells have been verified
      // preserve verified wells instead of overwriting them
      if (well?.offsetWells?.length > 0) return;

      const meters = (well?.fpzRadiuses && well?.fpzRadiuses.length > 0)
        ? well.fpzRadiuses[well.fpzRadiuses.length - 1]?.radius
        : 500; // default 500m
      const degrees = metersToDegrees(meters);

      const fpzPolygon = generateFpzPolygon(well, meters);
      console.log('fpzPolygon', fpzPolygon);

      const offsetWells = await gisActions.getCartofactWells({
        // filter: fpzState?.filter,
        boundingBox: fpzPolygon?.boundingBox ? padBboxDegrees(fpzPolygon.boundingBox, (meters * 3)) : null,
        orderBy: 'distance',
        fpzLine: {
          heelLat: well.heel.lat,
          heelLng: well.heel.lng,
          toeLat: well.toe.lat,
          toeLng: well.toe.lng,
          tvd: well.tvd || 0,
          distance: meters, // degrees,
        },
        abandoned: true,
      });

      const validDirectionalSurveys = offsetWells?.filter((offsetWell) => {
        // Check if lat and lng are valid
        const isValidLat = typeof offsetWell.lat === 'number' && offsetWell.lat >= -90 && offsetWell.lat <= 90;
        const isValidLng = typeof offsetWell.lng === 'number' && offsetWell.lng >= -180 && offsetWell.lng <= 180;
        if (isValidLat && isValidLng) {
          let bottomHoleLat, bottomHoleLng, survey, polylinePath;
          if (offsetWell.bottom_hole_coordinates) {
            const [latStr, lngStr] = offsetWell.bottom_hole_coordinates.split(',').map(coord => coord.trim());
            bottomHoleLat = parseFloat(latStr);
            bottomHoleLng = parseFloat(lngStr);
            offsetWell.bottomHoleLat = bottomHoleLat;
            offsetWell.bottomHoleLng = bottomHoleLng;

            survey = generateSimpleDirectionalSurvey({
              surfaceLat: offsetWell.lat,
              surfaceLng: offsetWell.lng,
              bottomHoleLat,
              bottomHoleLng,
              totalDepth: offsetWell.tvd,
              heelDepth: offsetWell.tvd, // default pass same value as totalDepth
              well: offsetWell
            });
            polylinePath = survey.coordinates;
          }
          offsetWell.polylinePath = offsetWell?.directional_survey || polylinePath;
          offsetWell.coordinates = survey?.coordinates || [];
          offsetWell.heel = survey?.heel || { lat: offsetWell.lat, lng: offsetWell.lng };
          offsetWell.toe = survey?.toe || { lat: bottomHoleLat, lng: bottomHoleLng };
          offsetWell.markerType = 'cartofactWell';
          if (survey?.coordinates && survey.coordinates.length > 0) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      });

      // dispatch({ type: 'SET_DIRECTIONAL_SURVEYS', payload: { directionalSurveys: validDirectionalSurveys || [], well } });
      wells[index].directionalSurveys = validDirectionalSurveys || [];

      // remove the treatment well from the offsetWells list
      const offsetWellsWithoutTreatment = validDirectionalSurveys.filter(offsetWell => {
        return offsetWell.unique_identifier !== well.unique_identifier;
      });

      // dispatch({ type: 'SET_OFFSET_WELLS', payload: { offsetWells: offsetWellsWithoutTreatment || [], well } });
      wells[index].offsetWells = offsetWellsWithoutTreatment || [];

      console.log('wells', wells);
      dispatch({
        type: 'SET_SELECTED_TREATMENT_WELLS',
        payload: { wells },
      });
    }


  };

  useEffect(() => {
    if (!selectedTreatmentWells || selectedTreatmentWells.length === 0) {
      navigate('/treatment'); // Redirect to home if no treatment wells selected
    }
    console.log('selectedTreatmentWells', selectedTreatmentWells);

    workingWellsRef.current = (selectedTreatmentWells ?? []).map((w) => ({
      ...w,
    }));

    fetchOffsetWells();
  }, []);

  useDeepCompareEffect(() => {
    console.log('useDeepCompareEffect selectedTreatmentWells[selected]', selectedTreatmentWells[selected]);
    let directionalSurveys = [];
    if (selected !== null && selected !== undefined) {
      directionalSurveys = selectedTreatmentWells[selected]?.directionalSurveys || [];
    } else {
      directionalSurveys = selectedTreatmentWells?.map(well => well?.directionalSurveys || []).flat();
    }
    console.log('useDeepCompareEffect directionalSurveys', directionalSurveys);
    if (directionalSurveys?.length > 0) {
      console.log('renderCube');
      renderCube(directionalSurveys);
    } else {
      console.log('setCubeRender null');
      setCubeRender(null);
    }
  }, [selectedTreatmentWells[selected]?.directionalSurveys, toggleFpzRadius, toggleOffsetWells, toggleRotate]);


  const handleTabChange = (event, newValue) => {
    if (newValue === 999) {
      console.log('Combined tab clicked', newValue);
      setTabIndex(newValue);
      setSelected(null);
    } else {
      setTabIndex(newValue);
      setSelected(newValue);
    }
  };

  const handleAddOffsetWell = (offsetWell) => {
    dispatch({ type: 'ADD_OFFSET_WELL', payload: { well: selectedTreatmentWells[selected], offsetWell: offsetWell } });
  };

  const handleDeleteOffsetWell = (index, well) => {
    dispatch({ type: 'DELETE_OFFSET_WELL', payload: { well: selectedTreatmentWells[selected], index: selected } });
  };

  const hasVerifiedOffsetWell = selectedTreatmentWells.some(treatment =>
    Array.isArray(treatment.offsetWells) &&
    treatment.offsetWells.some(offset => offset.verified === true)
  );

  const handleViewContacts = async () => {
    // check that there is at least one verified offset well
    if (!hasVerifiedOffsetWell) {
      alert('At least one offset well must be verified to view the report.');
      return false;
    }

    // Capture images of the cube
    const cubeImage = cubeRef.current.captureImage();

    const tile = document.querySelector(".gm-style > div:first-child > div");
    const transformMatrix = getComputedStyle(tile).transform;
    let left = 0, top = 0;

    const match = transformMatrix?.match(/matrix.*\((.+)\)/);
    if (match) {
      const values = match[1].split(",");
      left = parseFloat(values[4]);
      top = parseFloat(values[5]);
      tile.style.transform = "none";
      // tile.style.left = `${left}px`;
      // tile.style.top = `${top}px`;
    }

    // Capture canvas
    const canvas = await html2canvas(mapCardRef.current, {
      useCORS: true,
      ignoreElements: (el) => el.hasAttribute("data-html2canvas-ignore"),
    });

    // Restore transform
    if (match) {
      tile.style.transform = transformMatrix;
      tile.style.left = "";
      tile.style.top = "";
    }

    const mapImage = canvas.toDataURL("image/png");



    console.log("cubeImageUrl", cubeImageDataUrl, 'mapImageDataUrl', mapImageDataUrl);
    // store images in GCS and get public urls
    //const imageUrls = await fpzActions.uploadReportImages({
    //cubeImage: cubeImageDataUrl,
    //mapImage: mapImageDataUrl,
    //});

    dispatch({
      type: "SET_REPORT_IMAGES", payload: {
        mapImage: mapImage,
        cubeImage: cubeImage
      }
    });

    navigate('/app/contacts')
  };

  const handleToggleFpZRadius = (checked) => {
    console.log('handleToggleFpZRadius', checked);

  }

  const renderCube = (directionalSurveys) => {
    setCubeRender(null);
    setTimeout(() => {
      console.log('timeout renderCube');
      console.log('selectedTreatmentWells[0]', selectedTreatmentWells[0]);
      setCubeRender(
        <Cube
          directionalSurveys={directionalSurveys}
          selectedTreatmentWell={selectedTreatmentWells[selected]}
          toggleFpzRadius={toggleFpzRadius}
          toggleOffsetWells={toggleOffsetWells}
          setCubeImageDataUrl={setCubeImageDataUrl}
          toggleRotate={toggleRotate}
          ref={cubeRef}
        />
      );
    }, 500);
  };

  return (
    <Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Offset Wells
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => { navigate('/app/treatment') }}
          >
            Treatment(s)
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleViewContacts}
            disabled={hasVerifiedOffsetWell > 0 ? false : true}
          >
            Contacts
          </Button>
        </Box>
      </Box>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="inherit"
        variant="scrollable"
        scrollButtons="auto"
      >
        {selectedTreatmentWells.map((well, index) => (
          <Tab
            key={index}
            label={well?.unique_identifier}
          />
        ))}
        {(selectedTreatmentWells?.length > 1) && (
          <Tab
            key={999}
            value={999}
            label={`Combined*`}
            title="View Combined Treatment and Offset Wells"
          />
        )}
      </Tabs>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card id="map-card" ref={mapCardRef} sx={{ height: "100%", minHeight: 500 }}>
            <Box sx={{ position: "relative", height: "100%" }}>
              <Box
                data-html2canvas-ignore={true}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  backgroundColor: "transparent",
                  borderRadius: 1,
                  boxShadow: 2,
                  p: 1,
                  color: "white",
                }}
              >
                <Search />
                <div style={{ backgroundColor: "rgba(255, 255, 255, 0.5)", paddingLeft: "8px" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={toggleCartofactWells}
                        onChange={(e) => setToggleCartofactWells(e.target.checked)}
                        title="Toggle Unselected Wells"
                      />
                    }
                    label="Show All Wells"
                    sx={{ color: "black" }}
                  />
                </div>
              </Box>

              <Map
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={{ lat: 49.5, lng: -100 }}
                zoom={5}
                cartofactWells={cartofactWells}
                defaultFpzRadius={defaultFpzRadius}
                defaultFpzRadiusColor={defaultFpzRadiusColor}
                newCenter={mapCenter}
                newZoom={mapZoom}
                selectedTreatmentWells={selectedTreatmentWells}
                selected={selected}
                viewOffsetWells={true}
                mapOptions={{
                  gestureHandling: 'cooperative',
                }}
              />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card id="cube-card" sx={{ height: "100%", minHeight: 500 }}>
            <Box sx={{ position: "relative", height: "100%" }}>
              <Box
                data-html2canvas-ignore={true}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  borderRadius: 1,
                  boxShadow: 2,
                  p: 1,
                }}
              >
                <strong>TOGGLE</strong>
                <br />
                <Checkbox
                  checked={toggleFpzRadius}
                  onChange={(e) => setToggleFpzRadius(e.target.checked)}
                  title="Toggle FPZ Radius"
                />
                FPZ Radius
                <br />
                <Checkbox
                  checked={toggleOffsetWells}
                  onChange={(e) => setToggleOffsetWells(e.target.checked)}
                  title="Toggle Offset Wells"
                />
                Offset Wells
                <br />
                <Checkbox
                  checked={toggleRotate}
                  onChange={(e) => setToggleRotate(e.target.checked)}
                  title="Toggle Rotateion of Cube"
                />
                Rotate
              </Box>

              {cubeRender}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ flex: '0 0 auto', p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Shared Offset Wells - FPZ
            </Typography>
            <Box>
              <FpzTable
                selected={selected}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Bottom: Table full width */}


        {(cubeImageDataUrl && mapImageDataUrl) && (
          <div>
            <h3>Captured Cube Image:</h3>
            <img src={cubeImageDataUrl} alt="Captured Cube" style={{ width: "300px", border: "1px solid #ccc" }} />
          </div>
        )}
      </Box>
    </Box>

  );
};

export default OffsetPage;
